import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SPORTS } from "@/lib/sports";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Intake() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", sport: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.sport) {
      toast.error("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/assessment/start`, form);
      sessionStorage.setItem("mindedge_session", data.session_id);
      sessionStorage.setItem("mindedge_user", JSON.stringify(form));
      navigate("/quiz");
    } catch (err) {
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Could not start the assessment. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Could not start the assessment.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F7F5] text-[#111827]">
      <header className="max-w-7xl mx-auto px-6 lg:px-12 py-6 flex items-center justify-between">
        <Link to="/" className="flex items-center" data-testid="brand-logo">
          <img
            src="https://customer-assets.emergentagent.com/job_sports-psych-assess/artifacts/c5xfaumz_Godspeed%20Logo%20Original%20-%20Maroon%20Final.png"
            alt="Godspeed Sports"
            className="w-[240px] h-[140px] rounded-md object-contain bg-black"
          />
        </Link>
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm font-semibold text-[#4B5563] hover:text-[#1C3F39] transition-colors"
          data-testid="back-to-home"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={2.5} /> Back
        </Link>
      </header>

      <motion.section
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="max-w-2xl mx-auto px-6 lg:px-12 py-12"
      >
        <span className="inline-block text-xs font-bold tracking-[0.22em] uppercase text-[#E25F38] mb-4">
          Step 1 of 3
        </span>
        <h1 className="font-display text-4xl md:text-5xl leading-tight mb-3">
          Before we begin.
        </h1>
        <p className="text-[#4B5563] mb-10 text-lg">
          A few quick details so we can personalize your assessment and insights.
        </p>

        <form
          onSubmit={handleSubmit}
          className="bg-white border border-[#E5E7EB] rounded-xl p-8 space-y-6"
          data-testid="intake-form"
        >
          <div>
            <Label htmlFor="name" className="text-xs uppercase tracking-[0.18em] font-bold">
              Full Name
            </Label>
            <Input
              id="name"
              data-testid="intake-name"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Alex Morgan"
              className="mt-2 h-12 border-[#E5E7EB] focus-visible:ring-[#1C3F39]"
            />
          </div>

          <div>
            <Label htmlFor="email" className="text-xs uppercase tracking-[0.18em] font-bold">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              data-testid="intake-email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="you@example.com"
              className="mt-2 h-12 border-[#E5E7EB] focus-visible:ring-[#1C3F39]"
            />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-[0.18em] font-bold">Your Sport</Label>
            <Select
              value={form.sport}
              onValueChange={(v) => setForm({ ...form, sport: v })}
            >
              <SelectTrigger
                className="mt-2 h-12 border-[#E5E7EB] focus:ring-[#1C3F39]"
                data-testid="intake-sport-trigger"
              >
                <SelectValue placeholder="Select your sport" />
              </SelectTrigger>
              <SelectContent>
                {SPORTS.map((s) => (
                  <SelectItem
                    key={s}
                    value={s}
                    data-testid={`sport-option-${s.replace(/\W+/g, "-").toLowerCase()}`}
                  >
                    {s}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <Button
            type="submit"
            disabled={loading}
            className="w-full h-14 bg-[#1C3F39] hover:bg-[#132A26] text-white text-base font-semibold rounded-full"
            data-testid="intake-submit"
          >
            {loading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                Start the Assessment
                <ArrowRight className="ml-2 w-5 h-5" strokeWidth={2.5} />
              </>
            )}
          </Button>

          <p className="text-xs text-[#4B5563] text-center pt-2">
            We use your email only to save your results. No spam.
          </p>
        </form>
      </motion.section>
    </div>
  );
}
